import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  withStyles,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import MButton from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { LoadingIndicator, ScoreIndicator } from "../../../../Component";
import {
  cancelCheckRequest,
  restartCheckRequest,
  restartCompletedCheck,
} from "../../../../API/Check";
import { getEntity } from "../../../../API/Entity";
import { recordTypes } from "../../../../API/config";
import { formatDate, getAttributeValue } from "../../../../Utilities/Functions";
import CheckBox from "../../../../Component/Forms/CheckboxWithLabel";
import { useTranslation } from "react-i18next";
import { styles } from "./styles";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const CancelChecks = (props) => {
  const { classes, isOpen, entityId, onCancel, onClose, hasRecordPermission } =
    props;

  const [loading, setLoading] = useState(true);
  const [load, setLoad] = useState(true);
  const [entity, setEntity] = useState();
  const [name, setName] = useState();
  const [canKycCancel, setCanKycCancel] = useState(false);
  const [canKycRestartCompleted, setCanKycRestartCompleted] = useState(false);
  const [canKycRestartCancelled, setCanKycRestartCancelled] = useState(false);
  const [canAmlCancel, setCanAmlCancel] = useState(false);
  const [canAmlRestartCompleted, setCanAmlRestartCompleted] = useState(false);
  const [canAmlRestartCancelled, setCanAmlRestartCancelled] = useState(false);

  const [cancelAssociatedKycChecks, setCancelAssociatedKycChecks] =
    useState(false);
  const [cancelAssociatedAmlChecks, setCancelAssociatedAmlChecks] =
    useState(false);
  const [submtted, setSubmtted] = useState();

  const { showMessage, showErrorMessage, showConfirmation } =
    useContext(ModalDialogContext);
  const { t } = useTranslation();

  useEffect(() => {
    const getCRState = async (cr) => {
      const { cancelled, process_flow_state, permissions } = cr || {};
      let cancel = false;
      let restartCompleted = false;
      let restartCancelled = false;
      if (cancelled) {
        restartCancelled = await hasRecordPermission(
          permissions,
          recordTypes.checkRequest.actions.restartCancelled.name,
        );
      } else {
        const isComplete =
          process_flow_state === "Approved" ||
          process_flow_state === "Proceed At Risk" ||
          process_flow_state === "Rejected" ||
          process_flow_state === "Expired";
        if (isComplete) {
          restartCompleted = await hasRecordPermission(
            permissions,
            recordTypes.checkRequest.actions.restartCompleted.name,
          );
        } else {
          cancel = await hasRecordPermission(
            permissions,
            recordTypes.checkRequest.actions.cancel.name,
          );
        }
      }
      return [cancel, restartCompleted, restartCancelled];
    };

    const setPermissions = async (data) => {
      const { checkRequests } = data;
      const kycRequest = checkRequests.find((cr) => cr.check_type === "KYC");
      const amlRequest = checkRequests.find((cr) => cr.check_type === "AML");

      const [kycCancel, kycRestartCompleted, kycRestartCancelled] =
        await getCRState(kycRequest);
      const [amlCancel, amlRestartCompleted, amlRestartCancelled] =
        await getCRState(amlRequest);

      setCanKycCancel(kycCancel);
      setCanKycRestartCompleted(kycRestartCompleted);
      setCanKycRestartCancelled(kycRestartCancelled);
      setCanAmlCancel(amlCancel);
      setCanAmlRestartCompleted(amlRestartCompleted);
      setCanAmlRestartCancelled(amlRestartCancelled);
    };

    const loadEntity = async () => {
      setLoading(true);
      try {
        const result = await getEntity({ entityId });
        const { data = {} } = result;
        setEntity(data);
        const attr = data.attributes.find((a) => a.id === "3");
        setName(getAttributeValue(attr) || "");
        setPermissions(data);
        setLoading(false);
      } catch (error) {
        showErrorMessage({ error });
        setLoading(false);
      }
      setLoad(false);
    };
    if (load) {
      loadEntity();
    }
  }, [load]);

  const handleClose = () => {
    onClose(submtted);
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const handleCancelAssociationsChange = async (e, checkType) => {
    if (checkType === "KYC") {
      setCancelAssociatedKycChecks(e.target.checked);
    } else {
      setCancelAssociatedAmlChecks(e.target.checked);
    }
  };

  const renderCheckData = (cr) => {
    const {
      check_type: checkType,
      process_flow_state,
      allocated_user_name,
      created_by_user_name,
      created_date,
    } = cr;
    const canCancel = checkType === "KYC" ? canKycCancel : canAmlCancel;
    const canRestartCancelled =
      checkType === "KYC" ? canKycRestartCancelled : canAmlRestartCancelled;
    const canRestartCompleted =
      checkType === "KYC" ? canKycRestartCompleted : canAmlRestartCompleted;

    const score =
      (entity.scores || []).find((s) => s.check_type === checkType) || {};
    return (
      <Card className={classes.card} variant="outlined">
        <CardHeader
          classes={{
            title: classes.cardTitle,
          }}
          title={t(`labels.checks.${checkType}`)}
        />
        <CardContent>
          <Grid
            container
            spacing={1}
            justify="space-between"
            direction="column"
          >
            <Grid item container spacing={2}>
              <Grid item>
                <font className={classes.boldFont}>
                  {t("labels.check.cancellation.workflowState")}
                </font>
                :
              </Grid>
              <Grid item>{process_flow_state}</Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item>
                <font className={classes.boldFont}>
                  {t("labels.check.cancellation.assignedTo")}
                </font>
                :
              </Grid>
              <Grid item>{allocated_user_name}</Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item>
                <font className={classes.boldFont}>
                  {t("labels.check.cancellation.createdBy")}
                </font>
                :
              </Grid>
              <Grid item>{created_by_user_name}</Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item>
                <font className={classes.boldFont}>
                  {t("labels.check.cancellation.createdOn")}
                </font>
                :
              </Grid>
              <Grid item>{formatDate(created_date)}</Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item>
                <font className={classes.boldFont}>
                  {t("labels.check.cancellation.score")}
                </font>
                :
              </Grid>
              <Grid item>
                <ScoreIndicator
                  score={score.accepted_score || score.guided_score}
                  expired={score.expired}
                />
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item>
                {canCancel ? (
                  <CheckBox
                    label={
                      <font className={classes.boldFont}>
                        {t("labels.check.cancellation.cancelAssociatedRecords")}
                      </font>
                    }
                    onChange={(e) =>
                      handleCancelAssociationsChange(e, checkType)
                    }
                    value={
                      checkType === "KYC"
                        ? cancelAssociatedKycChecks
                        : cancelAssociatedAmlChecks
                    }
                  />
                ) : (
                  <span>&nbsp;</span>
                )}
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item>
                {canCancel ? (
                  <MButton
                    color="primary"
                    variant="contained"
                    style={{ height: 40, marginRight: 15 }}
                    onClick={(event) => cancelRequest(cr)}
                  >
                    {t("labels.check.cancellation.cancleCheck", { checkType })}
                  </MButton>
                ) : canRestartCancelled || canRestartCompleted ? (
                  <MButton
                    color="primary"
                    variant="contained"
                    style={{ height: 40, marginRight: 15 }}
                    onClick={(event) => restartRequest(cr)}
                  >
                    {t("labels.check.cancellation.restartCheck", { checkType })}
                  </MButton>
                ) : (
                  <span className={classes.errorText}>
                    {t("labels.check.cancellation.completedCheck")}
                  </span>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const validateCancel = (cr) => {
    if (cr.check_type === "KYC") {
      const amlCr = (entity.checkRequests || []).find(
        (c) => c.check_type === "AML",
      );
      if (amlCr && !amlCr.cancelled) {
        showErrorMessage({
          message: t("messages.check.cancellation.cancelAmlError"),
        });
        return false;
      }
    }
    return true;
  };

  const cancelRequest = async (cr) => {
    if (validateCancel(cr)) {
      const cancelAssociations =
        cr.check_type === "KYC"
          ? cancelAssociatedKycChecks
          : cancelAssociatedAmlChecks;
      showConfirmation({
        message: cancelAssociations
          ? t("messages.check.cancellation.cancelWithAssocationsConfirm", {
              checkType: cr.check_type,
            })
          : t("messages.check.cancellation.cancelConfirm", {
              checkType: cr.check_type,
            }),
        onPressYes: async (event) => {
          setLoading(true);
          try {
            await cancelCheckRequest({
              requestId: cr.id,
              isCancelled: true,
              cancelAssociations,
            });
            showMessage({
              message: cancelAssociations
                ? t("messages.check.cancellation.cancelledWithAssociations", {
                    checkType: cr.check_type,
                  })
                : t("messages.check.cancellation.cancelled", {
                    checkType: cr.check_type,
                  }),
            });
            setCancelAssociatedKycChecks(false);
            setCancelAssociatedAmlChecks(false);
            setSubmtted(true);
            setLoad(true);
          } catch (error) {
            showErrorMessage({ error });
            setLoading(false);
          }
        },
      });
    }
  };

  const restartRequest = async (cr) => {
    showConfirmation({
      message: t("messages.check.initiation.restartConfirm"),
      onPressYes: async (event) => {
        const isCancelled =
          cr.check_type === "KYC"
            ? canKycRestartCancelled
            : canAmlRestartCancelled;
        setLoading(true);
        try {
          isCancelled
            ? await restartCheckRequest({
                requestId: cr.id,
              })
            : await restartCompletedCheck({
                requestId: cr.id,
              });

          showMessage({ message: t("messages.check.initiation.restarted") });
          setSubmtted(true);
          setLoad(true);
        } catch (error) {
          showErrorMessage({ error });
          setLoading(false);
        }
      },
    });
  };

  return (
    <Dialog
      disableBackdropClick
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      fullWidth={true}
      scroll="paper"
      maxWidth="md"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="customized-dialog-title" onClose={handleClose}>
        <Typography className={classes.panelTitle}>
          {t("labels.check.cancellation.header")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          renderLoadingIndicator()
        ) : (
          <Grid container justify="center" spacing={4}>
            <Grid item className={classes.recordTitle}>
              <span>
                {t("labels.check.cancellation.recordName")} : &nbsp;
                <Link to={`/viewCheck?id=${entity.id}`} target="_blank">
                  {name}
                </Link>
              </span>
            </Grid>
            <Grid item container justify="space-evenly">
              {(entity.checkRequests || []).map((cr, idx) => {
                return (
                  <Grid item key={idx}>
                    {renderCheckData(cr)}
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default withStyles(styles, { withTheme: true })(CancelChecks);
